import { useMemo, useRef, useState } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import {
  DataGridPro,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useGetLeakedSearchTermsQuery } from 'src/context/api/campaignLeaks/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import KpiCards, { KpiCardItem } from '../shared/kpi-cards';
import PlatformToggle, { platformLabel, useCampaignLeaksPlatform } from '../shared/platform-toggle';
import GridActions, { useGridPanels } from '../shared/grid-actions';
import { campaignLeaksGridProps } from '../shared/grid-config';
import { useCampaignLeaksRegions } from '../shared/regions';
import { applyFilters, applySort, isActiveFilter } from '../shared/grid-filtering';
import { useStoredColumns } from '../shared/use-stored-columns';
import { downloadCsv } from '../shared/csv';
import { DASH, formatDateRange, formatInteger, formatMoney } from '../shared/format';
import { EmptyState, ErrorState } from '../shared/states';
import { type CampaignLeaksT, useCampaignLeaksT } from '../shared/i18n';
import {
  CARD_SX,
  GRID_SX,
  TABLE_HEADER_SX,
  cappedGridHeight,
  rowsOnPage,
} from '../shared/grid-sx';
import { buildLeakedColumns } from './columns';
import {
  COLUMNS_STORAGE_KEY,
  CSV_FIELDS,
  DEFAULT_SORT,
  csvFilename,
  csvRows,
  defaultColumnVisibility,
  getLeakedAccessors,
  getLeakedFilterAccessors,
} from './utils';

export const leakedKpiItems = (
  kpis:
    | { leaking_terms: number; ad_cost: number; leaked_spend: number; leaked_cpc: number | null }
    | undefined,
  currency: string,
  ct: CampaignLeaksT
): KpiCardItem[] => [
  {
    key: 'leaking_terms',
    title: ct('kpis.leaking_terms.title'),
    definition: ct('kpis.leaking_terms.definition'),
    value: formatInteger(kpis?.leaking_terms ?? 0),
  },
  {
    key: 'ad_cost',
    title: ct('kpis.ad_cost.title'),
    definition: ct('kpis.ad_cost.definition'),
    value: formatMoney(kpis?.ad_cost ?? 0, currency),
  },
  {
    key: 'leaked_spend',
    title: ct('kpis.leaked_spend.title'),
    definition: ct('kpis.leaked_spend.definition'),
    value: formatMoney(kpis?.leaked_spend ?? 0, currency),
  },
  {
    key: 'leaked_cpc',
    title: ct('kpis.leaked_cpc.title'),
    definition: ct('kpis.leaked_cpc.definition'),
    value: kpis?.leaked_cpc == null ? DASH : formatMoney(kpis.leaked_cpc, currency, undefined, 2),
  },
];

export default function LeakedSearchTerms() {
  const workspace: any = useCurrentWorkspace();
  const platform = useCampaignLeaksPlatform();
  const { regionsParam } = useCampaignLeaksRegions();
  const currency = workspace?.currency || 'USD';
  const ct = useCampaignLeaksT();
  const leakedAccessors = useMemo(() => getLeakedAccessors(ct), [ct]);
  const filterAccessors = useMemo(() => getLeakedFilterAccessors(ct), [ct]);
  const { data, isFetching, isError, refetch } = useGetLeakedSearchTermsQuery(
    { client: workspace?.bqTable, platform, regions: regionsParam },
    { skip: !workspace?.bqTable }
  );
  // Results of the current platform only: no previous platform's rows while switching.
  const loading = isFetching || (!data && !isError);

  const apiRef = useRef<any>(null);
  const { onButtonDown, onToggle, panelSlotProps } = useGridPanels(apiRef);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const defaultModel = useMemo(defaultColumnVisibility, []);
  const [columnVisibilityModel, setColumnVisibilityModel] = useStoredColumns(
    COLUMNS_STORAGE_KEY,
    defaultModel
  );

  // Back to the first page on every sort, filter, platform or region change.
  const rowsKey = JSON.stringify({ items: filterModel.items, sortModel, platform, regionsParam });
  const [paginatedKey, setPaginatedKey] = useState(rowsKey);
  if (paginatedKey !== rowsKey) {
    setPaginatedKey(rowsKey);
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
  }

  const allRows = useMemo(() => data?.rows ?? [], [data]);
  const rows = useMemo(
    () =>
      applySort(
        applyFilters(allRows, filterModel.items, filterAccessors),
        sortModel,
        leakedAccessors
      ),
    [allRows, filterModel.items, sortModel, leakedAccessors, filterAccessors]
  );
  const maxCost = Math.max(0, ...rows.map((row) => row.ad_cost ?? 0));
  const maxClicks = Math.max(0, ...rows.map((row) => row.ad_clicks ?? 0));
  const columns = useMemo(
    () => buildLeakedColumns({ ct, rows: allRows, currency, maxCost, maxClicks }),
    [ct, allRows, currency, maxCost, maxClicks]
  );
  const activeFilters = filterModel.items.filter(isActiveFilter).length;

  const handleExport = () =>
    downloadCsv(
      csvFilename(workspace?.bqTable ?? '', platform, data?.window?.end),
      csvRows(rows),
      CSV_FIELDS
    );

  // "No Bing Ads data" only when the platform has no data at all, not when it has no leak.
  const noData = !loading && !isError && data && !data.has_platform_data && platform === 'bing_ads';
  const noLeak = !loading && !isError && data && allRows.length === 0;

  const renderBody = () => {
    if (isError) return <ErrorState onRetry={refetch} />;
    if (noData)
      return <EmptyState message={ct('leaked.no_platform_data', { platform: platformLabel(platform) })} />;
    if (noLeak)
      return (
        <EmptyState
          icon="material-symbols:shield-outline"
          message={ct('leaked.no_leak')}
        />
      );
    const cappedHeight = cappedGridHeight(
      rowsOnPage(loading ? 0 : rows.length, paginationModel.page, paginationModel.pageSize),
      0
    );
    return (
      <Box sx={{ height: cappedHeight ?? 'auto' }} data-testid="grid-box">
        <DataGridPro
          {...campaignLeaksGridProps({
            apiRef,
            panelSlotProps,
            defaultModel,
            slotProps: { loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } as any },
          })}
          autoHeight={cappedHeight === null}
          loading={loading}
          rows={loading ? [] : rows}
          columns={columns}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model.length ? model : DEFAULT_SORT)}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sx={GRID_SX}
        />
      </Box>
    );
  };

  return (
    <Stack spacing={3} sx={{ px: '24px', pb: 3, fontFamily: 'Public Sans' }}>
      <KpiCards items={leakedKpiItems(data?.kpis, currency, ct)} loading={loading} error={isError} />
      <Box sx={CARD_SX}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={TABLE_HEADER_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1.2, color: '#1C252E' }}
            >
              {ct('leaked.table_title')}
            </Typography>
            <Tooltip
              title={data?.window ? formatDateRange(data.window.start, data.window.end) : ''}
              placement="top"
            >
              <Box
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: '24px',
                  bgcolor: 'rgba(118, 53, 220, 0.12)',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1,
                    color: '#7635DC',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ct('leaked.last_7_days')}
                </Typography>
              </Box>
            </Tooltip>
          </Stack>
          <PlatformToggle />
          <GridActions
            activeFilters={activeFilters}
            onButtonDown={onButtonDown}
            onToggle={onToggle}
            onExport={handleExport}
            exportDisabled={loading || !rows.length}
          />
        </Stack>
        {renderBody()}
      </Box>
    </Stack>
  );
}
